/* Copyright (c) 2018-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { isNil, isEmpty } from 'lodash';
/* interfaces */
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { BridgeService } from 'crimson/lib/bridge/bridge.service';
import { SystemInfoService } from 'crimson/lib/system-info-service/system-info.service';
import { SystemInfo } from 'crimson/lib/system-info-service/system-info.models';
import { BridgeCommands } from 'bridge-commands/bridge.commands';
import {
    NvHttpEndpoint,
    NvRequestOptions,
    HeaderType,
    RetryConfig
} from 'nv-angular-http-endpoint/lib/nv-http-endpoint/nv-http-endpoint';
import { NvHttpEndpointFactoryService } from 'nv-angular-http-endpoint/lib/nv-http-endpoint-factory/nv-http-endpoint-factory.service';
import { AppConfig, BaseConfigType } from 'app-config/lib/app.config';
import { DistributedTracingUtilService } from 'distributed-tracing/lib/services/distributedtracing-util.service';
import { OpenTelemetrySpan } from 'distributed-tracing/lib/distributed-tracing';
import { SpanNames } from 'distributed-tracing/lib/constants/distributedtracing_constants';
import { StarfleetConfig } from '../starfleet-config';
import {
    AccessTokenResponse,
    AcessTokenRequest,
    DeviceAuthorizeResponse,
    StarfleetUser,
    ClientTokenResponse,
    KVStoreClientDataResponse
} from '../starfleet.interface';

const originalService = 'Starfleet';
const KVStoreOrigionalService = 'KVStore';

const DefaultStarfleetRetryConfig: RetryConfig = {
    defaultRetries: 2,
    defaultTimeout: 30000,
    defaultTimeBetweenRetries: 1000
};

@Injectable({
    providedIn: 'root'
})
export class StarfleetHttpService {
    logger: Logger;
    private endpointAccessToken: NvHttpEndpoint;
    private endpointClientToken: NvHttpEndpoint;
    private endpointUserInfo: NvHttpEndpoint;
    private endpointKVStore: NvHttpEndpoint;

    constructor(
        private starfleetConfig: StarfleetConfig,
        private endpointFactory: NvHttpEndpointFactoryService,
        private bridgeService: BridgeService,
        private systemInfoService: SystemInfoService,
        private appConfig: AppConfig<BaseConfigType>,
        loggingService: LoggingService,
        private distributedtracingUtilService: DistributedTracingUtilService
    ) {
        const config: RetryConfig = { ...DefaultStarfleetRetryConfig, ...this.starfleetConfig.retryConfig };

        const defaultEndpointConfig = {
            config
        };

        this.endpointAccessToken = this.endpointFactory.createEndpoint(defaultEndpointConfig);
        this.endpointClientToken = this.endpointFactory.createEndpoint(defaultEndpointConfig);
        this.endpointUserInfo = this.endpointFactory.createEndpoint(defaultEndpointConfig);
        this.endpointKVStore = this.endpointFactory.createEndpoint(defaultEndpointConfig);
        this.logger = loggingService.getLogger('libraries/starfleet-http.service');
    }

    requestDeviceAuthorize(
        deviceId: string,
        deviceName: string,
        idpId?: string,
        parentSpan?: OpenTelemetrySpan
    ): Observable<DeviceAuthorizeResponse> {
        let data = new HttpParams()
            .set('client_id', this.starfleetConfig.clientId)
            .set('scope', this.starfleetConfig.scope)
            .set('device_id', deviceId)
            .set('display_name', deviceName);
        if (idpId) {
            data = data.set('idp_id', idpId);
        }

        const options: NvRequestOptions = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8'
            },
            body: data.toString(),
            reportRequestData: {
                originalService,
                serviceUseCase: 'Device_Authorization'
            }
        };
        const url = `${this.starfleetConfig.url}/device/authorize`;
        const httpRequest = this.endpointAccessToken.post<DeviceAuthorizeResponse>(url, options);

        return this.distributedtracingUtilService.handleHttpEndpointSpan(
            httpRequest,
            options,
            SpanNames.StarfleetGetAccessToken,
            parentSpan
        );
    }
    /**
     * retrieved access token and id token from client token
     *
     * @param clientToken
     * @param sub subject id of logged in user
     */
    getAccessToken(clientToken: string, sub: string, parentSpan?: OpenTelemetrySpan): Observable<AccessTokenResponse> {
        if (isNil(clientToken) || clientToken.trim().length === 0) {
            return throwError(new Error('invalid or missing clientToken parameter'));
        }

        if (isNil(sub) || sub.trim().length === 0) {
            return throwError(new Error('invalid or missing sub parameter'));
        }

        // to do define interface for request data
        const data = new HttpParams()
            .set('grant_type', 'urn:ietf:params:oauth:grant-type:client_token')
            .set('client_token', clientToken)
            .set('client_id', this.starfleetConfig.clientId)
            .set('sub', sub);

        const options: NvRequestOptions = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8'
            },
            body: data.toString(),
            reportRequestData: {
                originalService,
                serviceUseCase: 'Get_Access_Token'
            }
        };
        const httpRequest = this.endpointAccessToken.post<AccessTokenResponse>(
            `${this.starfleetConfig.url}/token`,
            options
        );
        return this.distributedtracingUtilService.handleHttpEndpointSpan(
            httpRequest,
            options,
            SpanNames.StarfleetGetAccessToken,
            parentSpan
        );
    }

    /**
     * retrieved access token and id token from client token
     *
     * @param deviceCode
     */
    getAccessTokenFromDeviceCode(deviceCode: string, parentSpan?: OpenTelemetrySpan): Observable<AccessTokenResponse> {
        if (isNil(deviceCode) || deviceCode.trim().length === 0) {
            return throwError(new Error('invalid or missing clientToken parameter'));
        }

        // to do define interface for request data
        const data = new HttpParams()
            .set('grant_type', 'urn:ietf:params:oauth:grant-type:device_code')
            .set('device_code', deviceCode)
            .set('client_id', this.starfleetConfig.clientId);

        const options: NvRequestOptions = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8'
            },
            body: data.toString(),
            reportRequestData: {
                originalService,
                serviceUseCase: 'Get_Access_Token'
            }
        };
        const httpRequest = this.endpointAccessToken.post<AccessTokenResponse>(
            `${this.starfleetConfig.url}/token`,
            options
        );
        return this.distributedtracingUtilService.handleHttpEndpointSpan(
            httpRequest,
            options,
            SpanNames.StarfleetGetAccessToken,
            parentSpan
        );
    }
    /**
     * Retrieve userInfo using accessToken
     *
     * @param accessToken
     */
    getUserInfo(accessToken: string, parentSpan?: OpenTelemetrySpan): Observable<StarfleetUser> {
        if (isNil(accessToken) || accessToken.trim().length === 0) {
            return throwError(new Error('invalid or missing accessToken parameter'));
        }

        const options: NvRequestOptions = {
            headers: this.getStarfleetAuthorizeHeaders(accessToken),
            reportRequestData: {
                originalService,
                serviceUseCase: 'Get_User_Info'
            }
        };
        const httpRequest = this.endpointUserInfo.get<StarfleetUser>(`${this.starfleetConfig.url}/userinfo`, options);
        return this.distributedtracingUtilService.handleHttpEndpointSpan(
            httpRequest,
            options,
            SpanNames.StarfleetGetUserInfo,
            parentSpan
        );
    }

    /**
     * Retrieve userInfo using accessToken
     *
     * @param accessToken
     */
    getClientToken(accessToken: string, parentSpan?: OpenTelemetrySpan): Observable<ClientTokenResponse> {
        if (isNil(accessToken) || accessToken.trim().length === 0) {
            return throwError(new Error('invalid or missing accessToken parameter'));
        }

        const options: NvRequestOptions = {
            headers: this.getStarfleetAuthorizeHeaders(accessToken),
            reportRequestData: {
                originalService,
                serviceUseCase: 'Get_Client_Token'
            }
        };
        const httpRequest = this.endpointClientToken.get<ClientTokenResponse>(
            `${this.starfleetConfig.url}/client_token`,
            options
        );
        return this.distributedtracingUtilService.handleHttpEndpointSpan(
            httpRequest,
            options,
            SpanNames.StarfleetGetClientToken,
            parentSpan
        );
    }

    /**
     * Redeem authorization code
     *
     * @param code
     */
    redeemAuthCode(
        code: string,
        redirectUri: string,
        codeVerifier: string,
        parentSpan?: OpenTelemetrySpan
    ): Observable<AccessTokenResponse> {
        if (!code?.trim()?.length) {
            return throwError(new Error('invalid or missing auth code parameter'));
        }

        if (!redirectUri?.trim()?.length) {
            return throwError(new Error('invalid or missing redirectUri parameter'));
        }

        if (!codeVerifier?.trim()?.length) {
            return throwError(new Error('invalid or missing codeVerifier parameter'));
        }

        // to do define interface for request data
        const data = new HttpParams()
            .set('grant_type', 'authorization_code')
            .set('code', code)
            .set('redirect_uri', redirectUri)
            .set('code_verifier', codeVerifier);

        const options: NvRequestOptions = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8'
            },
            body: data.toString(),
            reportRequestData: {
                originalService,
                serviceUseCase: 'Get_Access_Token'
            }
        };
        const httpRequest = this.endpointAccessToken.post<AccessTokenResponse>(
            `${this.starfleetConfig.url}/token`,
            options
        );
        return this.distributedtracingUtilService.handleHttpEndpointSpan(
            httpRequest,
            options,
            SpanNames.StarfleetGetAccessToken,
            parentSpan
        );
    }

    /**
     * Invalidate Access Tokens and Client Token at user logout
     *
     * @param accessToken
     */
    deleteClientTokens(accessToken: string, parentSpan?: OpenTelemetrySpan): Observable<void> {
        if (isNil(accessToken) || accessToken.trim().length === 0) {
            return throwError(new Error('invalid or missing accessToken parameter'));
        }

        const options: NvRequestOptions = {
            params: {
                level: 'client'
            },
            headers: this.getStarfleetAuthorizeHeaders(accessToken),
            reportRequestData: {
                originalService,
                serviceUseCase: 'Delete_Client_Data' // temp usage till schema is updated for next release
            }
        };
        const httpRequest = this.endpointClientToken.delete<void>(
            `${this.starfleetConfig.url}/assets/v2/Tokens`,
            options
        );
        return this.distributedtracingUtilService.handleHttpEndpointSpan(
            httpRequest,
            options,
            SpanNames.StarfleetDeleteClientToken,
            parentSpan
        );
    }

    /**
     * @param accessToken
     */
    private getStarfleetAuthorizeHeaders(accessToken: string) {
        return {
            [HeaderType.authorization]: `Bearer ${accessToken}`
        };
    }

    // key value store endpoints
    getClientData(
        idToken: string,
        keys?: string[],
        parentSpan?: OpenTelemetrySpan
    ): Observable<KVStoreClientDataResponse> {
        if (isNil(idToken) || idToken.trim().length === 0) {
            return throwError(new Error('invalid or missing idToken parameter'));
        }

        const options: NvRequestOptions = {
            headers: this.getKVStoreAuthorizeHeaders(idToken),
            reportRequestData: {
                originalService: KVStoreOrigionalService,
                serviceUseCase: 'Get_Client_Data'
            }
        };

        const url = this.formatKeyValStoreUrl(`${this.starfleetConfig.keyValueServiceUrl}/clientData`, keys);
        const httpRequest = this.endpointKVStore.get<KVStoreClientDataResponse>(url, options);
        return this.distributedtracingUtilService.handleHttpEndpointSpan<KVStoreClientDataResponse>(
            httpRequest,
            options,
            SpanNames.KVStoreGetClientData,
            parentSpan
        );
    }

    deleteClientData(idToken: string, key: string, values?: string[], parentSpan?: OpenTelemetrySpan): Observable<any> {
        if (isNil(idToken) || idToken.trim().length === 0) {
            return throwError(new Error('invalid or missing idToken parameter'));
        }

        const options: NvRequestOptions = {
            headers: this.getKVStoreAuthorizeHeaders(idToken),
            reportRequestData: {
                originalService: KVStoreOrigionalService,
                serviceUseCase: 'Delete_Client_Data'
            }
        };

        const url = this.formatKeyValStoreUrl(`${this.starfleetConfig.keyValueServiceUrl}/clientData`, [key], values);
        const httpRequest = this.endpointKVStore.delete<any>(url, options);
        return this.distributedtracingUtilService.handleHttpEndpointSpan<KVStoreClientDataResponse>(
            httpRequest,
            options,
            SpanNames.KVStoreDeleteClientData,
            parentSpan
        );
    }

    setClientData(idToken: string, key: string, value: string[], parentSpan?: OpenTelemetrySpan): Observable<any> {
        if (isNil(idToken) || idToken.trim().length === 0) {
            return throwError(new Error('invalid or missing idToken parameter'));
        }

        const options: NvRequestOptions = {
            headers: this.getKVStoreAuthorizeHeaders(idToken),
            reportRequestData: {
                originalService: KVStoreOrigionalService,
                serviceUseCase: 'Set_Client_Data'
            },
            body: {
                key,
                value
            }
        };

        const httpRequest = this.endpointKVStore.post<any>(
            `${this.starfleetConfig.keyValueServiceUrl}/clientData`,
            options
        );
        return this.distributedtracingUtilService.handleHttpEndpointSpan<KVStoreClientDataResponse>(
            httpRequest,
            options,
            SpanNames.KVStoreSetClientData,
            parentSpan
        );
    }

    appendClientData(idToken: string, key: string, value: string[], parentSpan?: OpenTelemetrySpan): Observable<any> {
        if (isNil(idToken) || idToken.trim().length === 0) {
            return throwError(new Error('invalid or missing idToken parameter'));
        }

        const options: NvRequestOptions = {
            headers: this.getKVStoreAuthorizeHeaders(idToken),
            reportRequestData: {
                originalService: KVStoreOrigionalService,
                serviceUseCase: 'Set_Client_Data'
            },
            body: {
                key,
                value
            }
        };

        const httpRequest = this.endpointKVStore.put<any>(
            `${this.starfleetConfig.keyValueServiceUrl}/clientData`,
            options
        );
        return this.distributedtracingUtilService.handleHttpEndpointSpan<KVStoreClientDataResponse>(
            httpRequest,
            options,
            SpanNames.KVStoreAppendClientData,
            parentSpan
        );
    }

    private formatKeyValStoreUrl(url: string, keys: string[], values?: string[]): string {
        if (isEmpty(keys)) {
            return url;
        }

        const params = [];
        keys.forEach(elem => {
            params.push(`key=${elem}`);
        });

        if (values) {
            values.forEach(elem => {
                params.push(`value=${elem}`);
            });
        }

        return encodeURI(`${url}?${params.join('&')}`);
    }

    /**
     * @param idToken
     */
    private getKVStoreAuthorizeHeaders(idToken: string) {
        return {
            [HeaderType.authorization]: `GFNJWT ${idToken}`
        };
    }
}
